import type { IssueActivity } from '@atlassian/jira-forge-ui-types';
import type { ContextPanel } from '@atlassian/jira-issue-shared-types';

export const CONNECT_ENTITY_TYPE = 'CONNECT_ENTITY_TYPE' as const;
export const FORGE_ENTITY_TYPE = 'FORGE_ENTITY_TYPE' as const;

type StatusTypes = 'lozenge' | 'icon' | 'badge';
type LozengeTypes = 'default' | 'success' | 'removed' | 'inprogress' | 'new' | 'moved';

export type ConnectGlanceType = typeof CONNECT_ENTITY_TYPE;

export type GlanceIcon = {
	url: string;
	alt?: string;
};

// This is the status that is persisted in Jira entity properties by the relevant app. As this store
// holds arbitrary json there's no guarantees about what state the data will be in (as it in may be missing params).
export type GlanceStatus = {
	type?: StatusTypes | undefined;
	value?: {
		// this is the url for icon status,
		label?: string | undefined;
		// we could use type for alt of icon status
		type?: LozengeTypes | undefined;
	};
};

export type Glance = {
	name: string;
	appKey: string;
	moduleKey: string;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	options: any;
	target: string;
	type: string;
	icon: GlanceIcon;
	label: {
		type: 'text';
		value: string;
	};
	status: GlanceStatus;
	glanceType: ConnectGlanceType;
};

type ForgeEntityType = typeof FORGE_ENTITY_TYPE;

export type ForgeActivityType = ForgeEntityType;
export type ForgeGlanceType = ForgeEntityType;
export type ForgePanelType = ForgeEntityType;
export type ForgeActionType = ForgeEntityType;

export type ForgeActivity = {
	name: string;
	extension: IssueActivity;
	moduleKey: string;
	appKey: string;
	type: ForgeActivityType;
};

export type ConnectActivityType = typeof CONNECT_ENTITY_TYPE;

export type ConnectActivity = {
	name: string;
	appKey: string;
	moduleKey: string;
	options: {
		url: string;
		productCtx: string;
	};
	type: ConnectActivityType;
};

export type EcosystemActivity = ConnectActivity | ForgeActivity;

export type EcosystemGlanceType = ConnectGlanceType | ForgeGlanceType;

export type EcosystemContentPanel = {
	name: string;
	appKey: string;
	moduleKey: string;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	options: any;
	type: string;
	iconUrl?: string;
	tooltip?: string;
	showByDefault: boolean;
};

export type EcosystemGlances = {
	[appKey: string]: {
		[moduleKey: string]: Glance;
	};
};

export type EcosystemContextPanels = {
	[appKey: string]: {
		[moduleKey: string]: ContextPanel;
	};
};

export type EcosystemContentPanels = {
	[appKey: string]: {
		[moduleKey: string]: EcosystemContentPanel;
	};
};

// JIV-15751: Remove when cleaning up issue-view-remove-backgroundscripts-from-critical-fetch_6f6on
export type EcosystemBackgroundScript = {
	appKey: string;
	moduleKey: string;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	options: any;
	shouldReloadOnRefresh: boolean;
};

// JIV-15846 Remove when cleaning up issue-view-remove-activitypanels-from-critical-fetch_43ozj
export type EcosystemActivityPanels = {
	[appKey: string]: {
		[moduleKey: string]: ConnectActivity;
	};
};

// JIV-16397: Remove when cleaning up issue-view-remove-connect-operations-from-critical-fetch_vtk4w
export type EcosystemOperation = {
	name: string;
	url: string;
	tooltip?: string;
	styleClass?: string;
};

export type EcosystemExtensions = {
	selectedContentPanel?: string;
	activityPanels?: EcosystemActivityPanels; // JIV-15846 Remove when cleaning up issue-view-remove-activitypanels-from-critical-fetch_43ozj
	canInstallAddons: boolean;
	contentPanels: EcosystemContentPanels;
	// JIV-15751: Remove when cleaning up issue-view-remove-backgroundscripts-from-critical-fetch_6f6on
	backgroundScripts?: EcosystemBackgroundScript[];
	ecosystemOnDate: number;
	glances: EcosystemGlances;
	contextPanels: EcosystemContextPanels;
	operations?: EcosystemOperation[]; // JIV-16397: Remove when cleaning up issue-view-remove-connect-operations-from-critical-fetch_vtk4w
};

export type EcosystemTransformResult = {
	ecosystem: Partial<EcosystemExtensions>;
	issueContentPanels?: string[];
	forgeIssueProperties: {
		collapsedPanels?: string[];
	};
};
